import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  onSnapshot,
  query,
  where,
  serverTimestamp,
  Timestamp,
  addDoc,
} from "firebase/firestore";
import { FirebaseError } from "firebase/app";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { auth, db, storage } from "@/lib/firebase";
import { richTextToPlainText, richTextToHtml } from "@/lib/richText";
import type {
  SystemProductNote,
  PersonalProductNote,
  ProductNoteData,
  ProductFile,
  ChangeRecord,
} from "@/types/productNotes";

// Collection names
const SYSTEM_NOTES_COLLECTION = "product_notes_system";
const PERSONAL_NOTES_COLLECTION = "product_notes_personal";

// The dashboard admin tool writes productNotes/smNotes/customerNotes (and
// change-history `content`) as Tiptap rich-text JSON. Flatten everything to
// plain text on read so DsaLens's plain-text note UI never receives an object.
function normalizeHistory(history: unknown): ChangeRecord[] {
   return (Array.isArray(history) ? history : [])
     .filter(
       (r): r is ChangeRecord =>
         !!r && typeof r === "object" && !Array.isArray(r),
     )
     .map((r) => ({
       ...r,
       content: richTextToPlainText(
         (r as unknown as { content?: unknown }).content,
       ),
     }));
}

// The dashboard stores Rich Text and Markdown as independent drafts —
// `${prefix}RichText` (Tiptap JSON) and `${prefix}Markdown` (plain string) —
// with `${prefix}ActiveFormat` deciding which one the admin wants shown here.
// Older docs only have a single `${prefix}` field plus a `${prefix}Format`
// flag; fall back to that so pre-migration notes still render.
function resolveActiveNote(data: Record<string, unknown>, prefix: string): { content: unknown; format: "richtext" | "markdown" } {
  const legacyFormat = data[`${prefix}Format`] === "markdown" ? "markdown" : "richtext";
  const activeFormat = data[`${prefix}ActiveFormat`] === "markdown" ? "markdown"
    : data[`${prefix}ActiveFormat`] === "richtext" ? "richtext"
    : legacyFormat;
  const richText = data[`${prefix}RichText`] !== undefined ? data[`${prefix}RichText`] : (legacyFormat === "richtext" ? data[prefix] : undefined);
  const markdown = data[`${prefix}Markdown`] !== undefined ? data[`${prefix}Markdown`] : (legacyFormat === "markdown" ? data[prefix] : undefined);
  return { content: activeFormat === "markdown" ? markdown : richText, format: activeFormat };
}

function normalizeNoteData<T extends Record<string, unknown>>(data: T): T {
  const product = resolveActiveNote(data, "productNotes");
  const sm = resolveActiveNote(data, "smNotes");
  return {
    ...data,
    productNotesFormat: product.format,
    smNotesFormat: sm.format,
    productNotes: richTextToPlainText(product.content),
    productNotesHtml: product.format === "markdown" ? "" : richTextToHtml(product.content),
    smNotes: richTextToPlainText(sm.content),
    smNotesHtml: sm.format === "markdown" ? "" : richTextToHtml(sm.content),
    customerNotes: richTextToPlainText(data.customerNotes),
    productNotesHistory: normalizeHistory(data.productNotesHistory),
    smNotesHistory: normalizeHistory(data.smNotesHistory),
    customerNotesHistory: normalizeHistory(data.customerNotesHistory),
  };
}

/**
 * Subscribe to system product notes for a specific bank and loan type
 */
export const subscribeToSystemNotes = (
  bank: string,
  loanType: string,
  callback: (note: SystemProductNote | null) => void,
  city?: string,
): (() => void) => {
  const q = query(
    collection(db, SYSTEM_NOTES_COLLECTION),
    where("bank", "==", bank),
    where("loanType", "==", loanType),
    where("city", "==", city || ""),
  );

  return onSnapshot(
    q,
    (snapshot) => {
      if (snapshot.empty) {
        callback(null);
        return;
      }

      const docData = snapshot.docs[0];
      const data = normalizeNoteData(docData.data());

      callback({
        id: docData.id,
        ...data,
      } as SystemProductNote);
    },
    (error) => {
      console.error("Error subscribing to system notes:", error);
      callback(null);
    },
  );
};

/**
 * Subscribe to the system notes for a bank + loan type without pinning a city.
 *
 * `subscribeToSystemNotes` filters on an exact city value, so `city: ""` finds
 * only the national record and misses a bank whose notes were authored against
 * a specific city. That is the wrong behaviour for an AskBot deep link, which
 * carries a bank and a loan type but no city: the link would open an empty
 * panel even though the bot had just answered from a note.
 *
 * The preference order mirrors `resolveProductDoc` in chatbot/core/product.ts
 * (its no-city branch) so the app lands on the same document the bot read:
 * the national record first — stored as an empty city, a missing city, or the
 * "__all__" sentinel — then the first city-specific record.
 */
export const subscribeToSystemNotesAnyCity = (
  bank: string,
  loanType: string,
  callback: (note: SystemProductNote | null) => void,
): (() => void) => {
  const q = query(
    collection(db, SYSTEM_NOTES_COLLECTION),
    where("bank", "==", bank),
    where("loanType", "==", loanType),
  );

  return onSnapshot(
    q,
    (snapshot) => {
      if (snapshot.empty) {
        callback(null);
        return;
      }

      const isNational = (d: (typeof snapshot.docs)[number]) => {
        const city = (d.data() as { city?: string }).city;
        return !city || city === "" || city === "__all__";
      };

      const docData = snapshot.docs.find(isNational) ?? snapshot.docs[0];
      const data = normalizeNoteData(docData.data());

      callback({
        id: docData.id,
        ...data,
      } as SystemProductNote);
    },
    (error) => {
      console.error("Error subscribing to system notes:", error);
      callback(null);
    },
  );
};

/**
 * Subscribe to personal product notes for a specific user, bank, and loan type
 */
export const subscribeToPersonalNotes = (
  userId: string,
  bank: string,
  loanType: string,
  callback: (note: PersonalProductNote | null) => void,
  city?: string,
): (() => void) => {
  const q = query(
    collection(db, PERSONAL_NOTES_COLLECTION),
    where("userId", "==", userId),
    where("bank", "==", bank),
    where("loanType", "==", loanType),
    where("city", "==", city || ""),
  );

  return onSnapshot(
    q,
    (snapshot) => {
      if (snapshot.empty) {
        callback(null);
        return;
      }

      const docData = snapshot.docs[0];
      const data = normalizeNoteData(docData.data());

      callback({
        id: docData.id,
        ...data,
      } as PersonalProductNote);
    },
    (error) => {
      console.error("Error subscribing to personal notes:", error);
      callback(null);
    },
  );
};

/**
 * Update or create system product notes
 */
export const updateSystemNotes = async (
  bank: string,
  loanType: string,
  updates: Partial<ProductNoteData>,
  userId: string,
  city?: string,
): Promise<void> => {
  const persistUpdate = async () => {
    // Find existing document by bank+loanType+city combo
    const cityValue = city || "";
    const q = query(
      collection(db, SYSTEM_NOTES_COLLECTION),
      where("bank", "==", bank),
      where("loanType", "==", loanType),
      where("city", "==", cityValue),
    );

    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      // Create new document
      const newDoc: Record<string, any> = {
        bank,
        loanType,
        city: cityValue,
        productNotes: "",
        productNotesHistory: [],
        smNotes: "",
        smNotesHistory: [],
        customerNotes: "",
        customerNotesHistory: [],
        files: [],
        ...updates,
        createdBy: userId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        lastEditedBy: userId,
        lastEditedAt: serverTimestamp(),
      };

      await addDoc(collection(db, SYSTEM_NOTES_COLLECTION), newDoc);
    } else {
      // Update existing document
      const docRef = doc(db, SYSTEM_NOTES_COLLECTION, snapshot.docs[0].id);
      await updateDoc(docRef, {
        ...updates,
        updatedAt: serverTimestamp(),
        lastEditedBy: userId,
        lastEditedAt: serverTimestamp(),
      });
    }
  };

  try {
    await persistUpdate();
  } catch (error) {
    // Claims can be stale right after role changes; refresh token and retry once.
    if (
      error instanceof FirebaseError &&
      error.code === "permission-denied" &&
      auth.currentUser
    ) {
      try {
        await auth.currentUser.getIdToken(true);
        await persistUpdate();
        return;
      } catch (retryError) {
        console.error(
          "Error updating system notes after token refresh:",
          retryError,
        );
        throw new Error("Failed to update system notes");
      }
    }

    console.error("Error updating system notes:", error);
    throw new Error("Failed to update system notes");
  }
};

/**
 * Update or create personal product notes
 */
export const updatePersonalNotes = async (
  userId: string,
  bank: string,
  loanType: string,
  updates: Partial<ProductNoteData>,
  city?: string,
): Promise<void> => {
  try {
    // Find existing document by userId+bank+loanType+city combo
    const cityValue = city || "";
    const q = query(
      collection(db, PERSONAL_NOTES_COLLECTION),
      where("userId", "==", userId),
      where("bank", "==", bank),
      where("loanType", "==", loanType),
      where("city", "==", cityValue),
    );

    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      // Create new document
      const newDoc: Record<string, any> = {
        userId,
        bank,
        loanType,
        city: cityValue,
        productNotes: "",
        productNotesHistory: [],
        smNotes: "",
        smNotesHistory: [],
        customerNotes: "",
        customerNotesHistory: [],
        files: [],
        ...updates,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      await addDoc(collection(db, PERSONAL_NOTES_COLLECTION), newDoc);
    } else {
      // Update existing document
      const docRef = doc(db, PERSONAL_NOTES_COLLECTION, snapshot.docs[0].id);
      await updateDoc(docRef, {
        ...updates,
        updatedAt: serverTimestamp(),
      });
    }
  } catch (error) {
    console.error("Error updating personal notes:", error);
    throw new Error("Failed to update personal notes");
  }
};

/**
 * Upload a file for product notes
 */
export const uploadProductFile = async (
  file: File,
  isSystem: boolean,
  userId: string,
  bank: string,
  loanType: string,
): Promise<ProductFile> => {
  try {
    const timestamp = Date.now();
    const uniqueFilename = `${timestamp}-${file.name}`;

    // Create storage path based on system/personal
    const storagePath = isSystem
      ? `product_notes/system/${bank}/${loanType}/${uniqueFilename}`
      : `product_notes/personal/${userId}/${bank}/${loanType}/${uniqueFilename}`;

    const storageRef = ref(storage, storagePath);

    // Upload file
    const uploadResult = await uploadBytes(storageRef, file);

    // Get download URL
    const downloadURL = await getDownloadURL(uploadResult.ref);

    // Return file object
    const productFile: ProductFile = {
      id: `file_${timestamp}_${Math.random().toString(36).substr(2, 9)}`,
      name: file.name,
      type: file.type,
      size: file.size,
      url: downloadURL,
      uploadedAt: Timestamp.now(),
      uploadedBy: userId,
    };

    return productFile;
  } catch (error) {
    console.error("Error uploading product file:", error);
    throw new Error(
      `Failed to upload file: ${error instanceof Error ? error.message : "Unknown error"}`,
    );
  }
};

/**
 * Delete a file from Firebase Storage
 */
export const deleteProductFile = async (fileUrl: string): Promise<void> => {
  try {
    // Extract storage path from download URL
    const url = new URL(fileUrl);
    const pathMatch = url.pathname.match(/\/o\/(.+)$/);

    if (!pathMatch) {
      console.warn("Could not extract storage path from URL:", fileUrl);
      return;
    }

    const storagePath = decodeURIComponent(pathMatch[1]);
    const fileRef = ref(storage, storagePath);

    await deleteObject(fileRef);
  } catch (error) {
    console.error("Error deleting file from storage:", error);
    // Don't throw - allow the update to continue even if file deletion fails
  }
};
